<template>
    <flex-container
        align-items="center"
        class="lead-list-pagination"
        justify-content="space-between"
        :padding="Size.md"
        no-wrap
    >
        <flex-container :size="3">
            <text-decorator weight="bold">
                Resultaten {{ firstItemOffset }} - {{ lastItemOffset }} van {{ props.totalItems }}
            </text-decorator>
        </flex-container>
        <flex-container
            v-if="props.totalItems"
            :gap="Size.sm"
            :size="4"
            justify-content="center"
            no-wrap
        >
            <button
                v-if="page > 1"
                type="button"
                class="lead-list-pagination__arrow"
                @click="emit('filter', page - 1)"
            >
                <svg-icon
                    :icon="ChevronLeft"
                    :size="IconSize.xxs"
                    is-custom-icon
                />
            </button>
            <template v-for="i in pageList">
                <button
                    v-if="i !== 0"
                    :key="i"
                    :class="{
                        'lead-list-pagination__item': true,
                        'lead-list-pagination__item--active': i === page,
                    }"
                    type="button"
                    @click="emit('filter', i)"
                >
                    <text-decorator bold :size="FontSize.sm">
                        {{ i }}
                    </text-decorator>
                </button>
                <div
                    v-if="i == 0"
                    :key="i"
                    class="lead-list-pagination__dots"
                >
                    ...
                </div>
            </template>
            <button
                v-if="page < totalPages"
                type="button"
                class="lead-list-pagination__arrow"
                @click="emit('filter', page + 1)"
            >
                <svg-icon
                    :icon="ChevronRight"
                    :size="IconSize.xxs"
                    is-custom-icon
                />
            </button>
        </flex-container>
        <flex-container
            :size="3"
            justify-content="flex-end"
            align-items="center"
            :gap="Size.sm"
        >
            <text-decorator weight="bold">
                Rijen
            </text-decorator>
            <input-group
                v-model="itemsPerPage"
                inline
                @update:model-value="emit('itemsPerPageChange', $event)"
            >
                <template #input>
                    <select-input
                        :items="[10, 20, 30, 50, 100]"
                        vertical-position="top-out"
                        horizontal-position="center"
                        no-scroll
                    />
                </template>
            </input-group>
        </flex-container>
    </flex-container>
</template>

<script setup lang="ts">
    import ChevronLeft from '@keyvue/one-awesome-icons/icons/regular/chevron-left.svg?component';
    import ChevronRight from '@keyvue/one-awesome-icons/icons/regular/chevron-right.svg?component';
    import { computed, ref } from 'vue';
    import {
        FlexContainer,
        FontSize,
        IconSize,
        InputGroup,
        SelectInput,
        Size,
        SvgIcon,
        TextDecorator,
    } from '@keyvue/one-ui';

    const props = defineProps<{
        totalItems: number
        page: number
    }>();

    const itemsPerPage = ref(30);

    const totalPages = computed(() => Math.ceil(props.totalItems / itemsPerPage.value));
    const firstItemOffset = computed(() => (props.page - 1) * itemsPerPage.value + 1);
    const lastItemOffset = computed(() => Math.min(props.page * itemsPerPage.value, props.totalItems));

    const emit = defineEmits<{
        (e: 'filter', value: number | null): void;
        (e: 'itemsPerPageChange', value: number): void;
    }>();

    /** source; stackoverflow <3 */
    const pageList = computed(() => {
        const maxLength = 9;
        const { page } = props;

        function range(start, end) {
            return Array.from(Array(end - start + 1), (_, i) => i + start);
        }

        const sideWidth = maxLength < 11 ? 1 : 2;

        // eslint-disable-next-line no-bitwise
        const leftWidth = (maxLength - sideWidth * 2 - 3) >> 1;

        // eslint-disable-next-line no-bitwise
        const rightWidth = (maxLength - sideWidth * 2 - 2) >> 1;

        if (totalPages.value <= maxLength) {
            // no breaks in list
            return range(1, totalPages.value);
        }

        if (page <= maxLength - sideWidth - 1 - rightWidth) {
            // no break on left of page
            return range(1, maxLength - sideWidth - 1)
                .concat(0, range(totalPages.value - sideWidth + 1, totalPages.value));
        }

        if (page >= totalPages.value - sideWidth - 1 - rightWidth) {
            // no break on right of page
            return range(1, sideWidth)
                .concat(0, range(totalPages.value - sideWidth - 1 - rightWidth - leftWidth, totalPages.value));
        }

        // Breaks on both sides
        return range(1, sideWidth)
            .concat(
                0,
                range(page - leftWidth, page + rightWidth),
                0,
                range(totalPages.value - sideWidth + 1, totalPages.value),
            );
    });
</script>

<style scoped lang="scss">
.lead-list-pagination {
    &__item {
        &--active {
            color: color(brand-white);
            background-color: var(--theme-colors-default--primary);
        }
    }

    &__arrow {

        color: var(--theme-colors-default--primary);
        background-color: color(purple-100);
    }

    &__arrow,
    &__item {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 0;
        cursor: pointer;
        min-width: size(lg) / 2;
        min-height: size(lg) / 2;
        border-radius: size(sm);
    }

    &__dots {
        display: flex;
        box-sizing: border-box;
        align-items: center;
        justify-content: center;
        line-height: size(md);
    }
}
</style>
